import { useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Card, LivePill } from "../../ds";
import type { DeviceDetail } from "../../lib/api";
import { downloadB64 } from "../../lib/download";
import { useLogStream } from "../../lib/hooks/useLogStream";

// Sektion „Logs“: Live-Stream (SSE) + Snapshot-Pull. Bewusst ohne SectionCard —
// Logs haben keinen Server-Snapshot („Logs nur beim Zusehen“). Der Stream läuft
// nur, solange diese Ansicht offen und der Tab sichtbar ist (siehe useLogStream).

export function LogsSection({ device }: { device: DeviceDetail }) {
  const log = useLogStream(device.id);
  const [follow, setFollow] = useState(true);
  const boxRef = useRef<HTMLPreElement>(null);

  // Auto-Scroll, solange der Bediener unten steht.
  useEffect(() => {
    if (follow && boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [log.lines, follow]);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          Logs
          {log.live ? <LivePill label="Stream läuft" /> : <Badge variant="neutral">gestoppt</Badge>}
        </span>
      }
      subtitle={`${log.lines.length} Zeilen im Puffer (max. ${log.maxLines}) · der Stream läuft nur, solange diese Ansicht offen ist`}
      actions={
        <>
          <Button
            size="sm"
            variant="ghost"
            iconName="download"
            loading={log.pulling}
            disabled={!device.online}
            onClick={() => void log.pull()}
          >
            Snapshot holen
          </Button>
          <Button size="sm" variant="ghost" iconName="trash" onClick={log.clear}>
            Leeren
          </Button>
          <Button
            size="sm"
            variant={log.streaming ? "danger-outline" : "primary"}
            iconName={log.streaming ? "stop" : "play"}
            disabled={!device.online}
            onClick={log.toggle}
          >
            {log.streaming ? "Stop" : "Start"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {!device.online && (
          <Alert variant="neutral">
            Router offline — kein Live-Stream möglich. Der Puffer zeigt nur, was zuvor empfangen
            wurde.
          </Alert>
        )}
        {log.paused && (
          <Alert variant="info">
            Browser-Tab im Hintergrund — der Stream ist pausiert und läuft weiter, sobald der Tab
            wieder sichtbar ist.
          </Alert>
        )}
        {log.error && (
          <Alert variant="warning" dismissible onDismiss={log.clearError}>
            {log.error}
            {log.gzFallback && (
              <div className="mt-2">
                <Button
                  size="sm"
                  variant="outline"
                  iconName="download"
                  onClick={() =>
                    downloadB64(log.gzFallback ?? "", `${device.hostname ?? device.id}-logs.gz`)
                  }
                >
                  logs.gz herunterladen
                </Button>
              </div>
            )}
          </Alert>
        )}
        {!follow && (
          <div className="flex items-center justify-between gap-3 text-xs">
            <span className="text-content-3">Auto-Scroll pausiert (du hast hochgescrollt).</span>
            <Button
              size="sm"
              variant="ghost"
              iconName="chevronDown"
              onClick={() => setFollow(true)}
            >
              Wieder folgen
            </Button>
          </div>
        )}
        <pre
          ref={boxRef}
          onScroll={() => {
            const el = boxRef.current;
            if (!el) return;
            setFollow(el.scrollHeight - el.scrollTop - el.clientHeight < 24);
          }}
          className="font-mono h-64 overflow-auto rounded-md bg-sunken p-3 text-xs leading-relaxed text-content-2 sm:h-96"
        >
          {log.lines.length === 0
            ? "Noch nichts empfangen — „Start“ öffnet den Live-Stream, „Snapshot holen“ zieht die letzten 500 Zeilen."
            : log.lines.join("\n")}
        </pre>
      </div>
    </Card>
  );
}
